import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, euro } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Download, Filter } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#4A5D4E", "#C07F67", "#6E8B5C", "#A36F5C", "#8FA88A", "#D9A382", "#6E7772"];

export default function PatientStats() {
  const { centers } = useAuth();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [centerId, setCenterId] = useState("all");
  const [data, setData] = useState(null);
  const [followUps, setFollowUps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (centerId && centerId !== "all") params.center_id = centerId;
      const [r, fu] = await Promise.all([
        api.get("/stats/patients", { params }),
        api.get("/patients/follow-ups/annual-review", {
          params: centerId !== "all" ? { center_id: centerId } : {},
        }).catch(() => ({ data: { count: 0, patients: [] } })),
      ]);
      setData(r.data);
      setFollowUps(fu.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const sourcePieData = useMemo(() => {
    if (!data?.by_source) return [];
    return data.by_source
      .filter((r) => (r.count ?? r.patients) > 0)
      .map((r) => ({ name: r.label, value: r.count ?? r.patients }));
  }, [data]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [["Sección", "Categoría", "Valor", "Conversión %", "Facturado €"]];
    rows.push(["TOTAL", "Pacientes totales", data.total_patients, "", ""]);
    rows.push([]);
    rows.push(["—— Por procedencia ——"]);
    data.conversion_by_source.forEach((r) => {
      rows.push(["Procedencia", r.label, r.patients, r.conversion_rate, r.billed]);
    });
    rows.push([]);
    rows.push(["—— Por población ——"]);
    data.by_city.forEach((r) => {
      rows.push(["Población", r.city, r.count, "", ""]);
    });
    rows.push([]);
    rows.push(["—— Cruzada población × procedencia ——"]);
    data.by_city_source.forEach((row) => {
      rows.push(["Población · Total", row.city, row.total, "", ""]);
      row.breakdown.forEach((b) => {
        rows.push([`  ${row.city}`, b.label, b.count, "", ""]);
      });
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `estadisticas-pacientes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="patient-stats-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-[#E5E3DB] pb-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Marketing & CRM
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Estadísticas de pacientes
          </h1>
          <p className="text-sm text-[#6E7772] mt-1">
            Procedencia, distribución geográfica, conversión y demografía.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={exportCSV}
          data-testid="export-stats-csv"
          disabled={!data}
        >
          <Download className="h-4 w-4 mr-2" strokeWidth={1.5} /> Exportar CSV
        </Button>
      </div>

      {/* Filtros */}
      <Card className="bg-white border-[#E5E3DB] shadow-none">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <Filter className="h-4 w-4 text-[#6E7772] mb-2.5" strokeWidth={1.5} />
            <div>
              <Label className="text-xs">Desde</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="stats-date-from"
              />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="stats-date-to"
              />
            </div>
            <div className="min-w-[180px]">
              <Label className="text-xs">Centro</Label>
              <Select value={centerId} onValueChange={setCenterId}>
                <SelectTrigger data-testid="stats-center-filter" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los centros</SelectItem>
                  {centers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={load}
              disabled={loading}
              data-testid="stats-apply-filters"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              {loading ? "Cargando…" : "Aplicar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {!data ? (
        <Card className="bg-white border-[#E5E3DB] shadow-none">
          <CardContent className="p-12 text-center text-[#6E7772]">
            Cargando estadísticas…
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Resumen */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard label="Pacientes (filtro)" value={data.total_patients} testid="stat-total" />
            <SummaryCard
              label="Procedencias distintas"
              value={data.by_source.filter((r) => (r.count ?? r.patients) > 0).length}
              testid="stat-sources"
            />
            <SummaryCard label="Poblaciones distintas" value={data.by_city.length} testid="stat-cities" />
            <SummaryCard
              label="Revisiones pendientes"
              value={followUps?.count || 0}
              accent="warning"
              testid="stat-followups"
            />
          </div>

          <Tabs defaultValue="source" className="w-full">
            <TabsList className="bg-[#EAE6DF] flex-wrap h-auto">
              <TabsTrigger value="source" data-testid="tab-stat-source">Procedencia</TabsTrigger>
              <TabsTrigger value="city" data-testid="tab-stat-city">Población</TabsTrigger>
              <TabsTrigger value="cross" data-testid="tab-stat-cross">Población × Procedencia</TabsTrigger>
              <TabsTrigger value="conversion" data-testid="tab-stat-conversion">Conversión</TabsTrigger>
              <TabsTrigger value="demo" data-testid="tab-stat-demo">Demografía</TabsTrigger>
              <TabsTrigger value="followups" data-testid="tab-stat-followups">Revisiones</TabsTrigger>
            </TabsList>

            {/* Procedencia */}
            <TabsContent value="source" className="mt-6 space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-white border-[#E5E3DB] shadow-none">
                  <CardHeader>
                    <CardTitle className="text-base font-heading font-medium">Distribución por canal</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={sourcePieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={90}
                            label={(e) => `${e.name} (${e.value})`}
                          >
                            {sourcePieData.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white border-[#E5E3DB] shadow-none">
                  <CardHeader>
                    <CardTitle className="text-base font-heading font-medium">Ranking</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-[#6E7772] border-b border-[#E5E3DB]">
                          <th className="py-2">Canal</th>
                          <th className="py-2 text-right">Pacientes</th>
                          <th className="py-2 text-right">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.by_source
                          .filter((r) => (r.count ?? r.patients) > 0)
                          .map((r) => {
                            const v = r.count ?? r.patients;
                            const pct = data.total_patients ? Math.round((v / data.total_patients) * 100) : 0;
                            return (
                              <tr key={r.source} className="border-b border-[#F1EEE8]" data-testid={`source-row-${r.source}`}>
                                <td className="py-2">{r.label}</td>
                                <td className="py-2 text-right font-medium">{v}</td>
                                <td className="py-2 text-right text-[#6E7772]">{pct}%</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Población */}
            <TabsContent value="city" className="mt-6">
              <Card className="bg-white border-[#E5E3DB] shadow-none">
                <CardHeader>
                  <CardTitle className="text-base font-heading font-medium">
                    Top poblaciones ({data.by_city.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.by_city.length === 0 ? (
                    <div className="text-center text-[#6E7772] py-8">
                      Sin datos de población. Asegúrate de rellenar el campo «Población» en las fichas de paciente.
                    </div>
                  ) : (
                    <div className="h-96">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.by_city.slice(0, 20)} layout="vertical" margin={{ left: 60, right: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" />
                          <XAxis type="number" stroke="#6E7772" />
                          <YAxis dataKey="city" type="category" stroke="#6E7772" width={120} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#4A5D4E" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Población × Procedencia */}
            <TabsContent value="cross" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="bg-white border-[#E5E3DB] shadow-none lg:col-span-1">
                  <CardHeader>
                    <CardTitle className="text-base font-heading font-medium">Poblaciones</CardTitle>
                  </CardHeader>
                  <CardContent className="max-h-[500px] overflow-y-auto scrollbar-thin">
                    {data.by_city_source.length === 0 ? (
                      <div className="text-center text-[#6E7772] py-4 text-sm">Sin datos</div>
                    ) : (
                      <div className="space-y-1">
                        {data.by_city_source.map((c) => (
                          <button
                            key={c.city}
                            onClick={() => setSelectedCity(c)}
                            data-testid={`cross-city-${c.city}`}
                            data-stats-city-row={c.city}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between ${
                              selectedCity?.city === c.city
                                ? "bg-[#4A5D4E] text-white"
                                : "bg-[#F7F6F3] hover:bg-[#EAE6DF] text-[#2A2F2C]"
                            }`}
                          >
                            <span>{c.city}</span>
                            <Badge className={selectedCity?.city === c.city ? "bg-white text-[#2A2F2C]" : "bg-[#EAE6DF] text-[#2A2F2C]"}>
                              {c.total}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card className="bg-white border-[#E5E3DB] shadow-none lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base font-heading font-medium">
                      {selectedCity ? `Procedencia de pacientes en ${selectedCity.city}` : "Selecciona una población"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!selectedCity ? (
                      <div className="text-center text-[#6E7772] py-12 text-sm">
                        Pincha una población a la izquierda para ver el desglose por canal.
                      </div>
                    ) : (
                      <>
                        <div className="h-64 mb-4">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={selectedCity.breakdown}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" />
                              <XAxis dataKey="label" stroke="#6E7772" />
                              <YAxis stroke="#6E7772" />
                              <Tooltip />
                              <Bar dataKey="count" fill="#C07F67" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs uppercase tracking-wider text-[#6E7772] border-b border-[#E5E3DB]">
                              <th className="py-2">Canal</th>
                              <th className="py-2 text-right">Pacientes</th>
                              <th className="py-2 text-right">%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedCity.breakdown.map((b) => {
                              const pct = selectedCity.total ? Math.round((b.count / selectedCity.total) * 100) : 0;
                              return (
                                <tr key={b.source} className="border-b border-[#F1EEE8]">
                                  <td className="py-2">{b.label}</td>
                                  <td className="py-2 text-right font-medium">{b.count}</td>
                                  <td className="py-2 text-right text-[#6E7772]">{pct}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Conversión */}
            <TabsContent value="conversion" className="mt-6">
              <Card className="bg-white border-[#E5E3DB] shadow-none">
                <CardHeader>
                  <CardTitle className="text-base font-heading font-medium">
                    Conversión & facturación por canal
                  </CardTitle>
                  <p className="text-xs text-[#6E7772] mt-1">
                    De cada canal, cuántos pacientes han comprado audífono y total facturado (facturas emitidas/pagadas).
                  </p>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-[#6E7772] border-b border-[#E5E3DB]">
                        <th className="py-2">Canal</th>
                        <th className="py-2 text-right">Pacientes</th>
                        <th className="py-2 text-right">Compraron</th>
                        <th className="py-2 text-right">Conversión</th>
                        <th className="py-2 text-right">Facturado</th>
                        <th className="py-2 text-right">Por paciente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.conversion_by_source
                        .filter((r) => r.patients > 0)
                        .map((r) => (
                          <tr key={r.source} className="border-b border-[#F1EEE8]" data-testid={`conv-row-${r.source}`}>
                            <td className="py-2">{r.label}</td>
                            <td className="py-2 text-right">{r.patients}</td>
                            <td className="py-2 text-right">{r.converted}</td>
                            <td className="py-2 text-right">
                              <Badge className={
                                r.conversion_rate >= 40 ? "bg-[#D1E0D7] text-[#2A2F2C]" :
                                r.conversion_rate >= 20 ? "bg-[#F1E5C0] text-[#2A2F2C]" :
                                "bg-[#E8D1D1] text-[#2A2F2C]"
                              }>
                                {r.conversion_rate}%
                              </Badge>
                            </td>
                            <td className="py-2 text-right font-medium">{euro(r.billed)}</td>
                            <td className="py-2 text-right text-[#6E7772]">{r.patients ? euro(r.billed / r.patients) : "—"}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Demografía */}
            <TabsContent value="demo" className="mt-6">
              <Card className="bg-white border-[#E5E3DB] shadow-none">
                <CardHeader>
                  <CardTitle className="text-base font-heading font-medium">Pirámide demográfica</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.demographic_pyramid} layout="vertical" margin={{ left: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" />
                        <XAxis type="number" stroke="#6E7772" />
                        <YAxis dataKey="bucket" type="category" stroke="#6E7772" />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="female" stackId="a" fill="#C07F67" name="Mujer" />
                        <Bar dataKey="male" stackId="a" fill="#4A5D4E" name="Hombre" />
                        <Bar dataKey="other" stackId="a" fill="#9CA3AF" name="Otro / sin dato" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Revisiones anuales pendientes */}
            <TabsContent value="followups" className="mt-6">
              <Card className="bg-white border-[#E5E3DB] shadow-none">
                <CardHeader>
                  <CardTitle className="text-base font-heading font-medium">
                    Pacientes pendientes de revisión anual
                  </CardTitle>
                  <p className="text-xs text-[#6E7772] mt-1">
                    Audífonos adaptados hace +11 meses sin contacto en los últimos 4 meses. Ideal para campaña de revisión y re-venta.
                  </p>
                </CardHeader>
                <CardContent>
                  {!followUps || followUps.count === 0 ? (
                    <div className="text-center text-[#6E7772] py-8">
                      No hay revisiones pendientes. ¡Todo al día!
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-[#6E7772] border-b border-[#E5E3DB]">
                          <th className="py-2">Paciente</th>
                          <th className="py-2">Teléfono</th>
                          <th className="py-2">Población</th>
                          <th className="py-2">Audífono</th>
                          <th className="py-2 text-right">Adaptado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {followUps.patients.map((p) => (
                          <tr key={p.patient_id} className="border-b border-[#F1EEE8]">
                            <td className="py-2">
                              <a
                                href={`/pacientes/${p.patient_id}`}
                                className="text-[#4A5D4E] hover:underline"
                              >
                                {p.name} {p.surname}
                              </a>
                            </td>
                            <td className="py-2">{p.phone || "—"}</td>
                            <td className="py-2">{p.city || "—"}</td>
                            <td className="py-2">{p.hearing_aid}</td>
                            <td className="py-2 text-right text-[#6E7772]">{p.last_fitting_date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent, testid }) {
  const bg = accent === "warning" ? "bg-[#F1E5C0]" : "bg-white";
  return (
    <Card className={`${bg} border-[#E5E3DB] shadow-none`}>
      <CardContent className="p-4">
        <div className="text-xs tracking-wider uppercase text-[#6E7772]">{label}</div>
        <div className="text-2xl font-heading font-medium text-[#2A2F2C] mt-1" data-testid={testid}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
